/**
 * Base API Client
 * Provides common API functionality with authentication and error handling
 */

import { authGuard, AuthState } from './auth-guard.js';

export interface Endpoints {
  [key: string]: string;
}

export interface APIOptions extends RequestInit {
  pathParams?: (string | number)[];
  queryParams?: Record<string, string | number | boolean | undefined | null>;
  endpoint?: string;
  rawResponse?: boolean;
}

export type APIErrorKind = 'http' | 'network' | 'auth' | 'unknown';

export class APIError extends Error {
  status?: number;
  payload?: unknown;
  responseText?: string;
  kind: APIErrorKind;
  isNetworkError: boolean;
  isAuthError: boolean;

  constructor(
    message: string,
    options: {
      status?: number;
      payload?: unknown;
      responseText?: string;
      kind?: APIErrorKind;
      cause?: unknown;
    } = {}
  ) {
    super(message);
    this.name = 'APIError';
    this.status = options.status;
    this.payload = options.payload;
    this.responseText = options.responseText;
    this.kind = options.kind || 'unknown';
    this.isNetworkError = this.kind === 'network';
    this.isAuthError = this.kind === 'auth';
    if (options.cause !== undefined) {
      (this as any).cause = options.cause;
    }
  }
}

export async function parseApiResponse<T = any>(response: Response): Promise<T> {
  if (response.status === 204) {
    return {} as T;
  }

  const contentType = response.headers.get('Content-Type') || '';
  if (!contentType || !contentType.includes('application/json')) {
    const text = await response.text();
    try {
      return text ? JSON.parse(text) : {} as T;
    } catch (_parseError) {
      return {} as T;
    }
  }

  return await response.json() as T;
}

async function parseApiErrorPayload(response: Response): Promise<{ payload?: unknown; responseText?: string; message: string }> {
  const responseText = await response.text();

  if (!responseText) {
    return {
      responseText: '',
      message: `API Error: ${response.status}`
    };
  }

  try {
    const payload = JSON.parse(responseText);
    const payloadMessage =
      typeof (payload as any)?.error === 'string' ? (payload as any).error :
      typeof (payload as any)?.message === 'string' ? (payload as any).message :
      null;

    return {
      payload,
      responseText,
      message: payloadMessage ? `API Error: ${response.status} - ${payloadMessage}` : `API Error: ${response.status} - ${responseText}`
    };
  } catch (_parseError) {
    return {
      responseText,
      message: `API Error: ${response.status} - ${responseText}`
    };
  }
}

export async function buildApiErrorFromResponse(response: Response): Promise<APIError> {
  const { payload, responseText, message } = await parseApiErrorPayload(response);
  return new APIError(message, {
    status: response.status,
    payload,
    responseText,
    kind: response.status === 401 ? 'auth' : 'http'
  });
}

export function isAbortLikeError(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false;
  }

  if (error.name === 'AbortError') {
    return true;
  }

  const cause = (error as Error & { cause?: unknown }).cause;
  return cause instanceof Error && cause.name === 'AbortError';
}

export function normalizeApiError(error: unknown): APIError {
  if (error instanceof APIError) {
    return error;
  }

  const message =
    error instanceof Error ? error.message :
    (error && typeof error === 'object' && typeof (error as any).message === 'string') ? (error as any).message :
    String(error || 'Unknown error');

  if (message.includes('not authenticated')) {
    return new APIError('Please log in to access this feature', {
      kind: 'auth',
      cause: error
    });
  }

  if (error instanceof TypeError) {
    return new APIError(message || 'Network request failed', {
      kind: 'network',
      cause: error
    });
  }

  return new APIError(message || 'Request failed', {
    kind: 'unknown',
    cause: error
  });
}

export function getApiErrorMessage(err: unknown, fallback: string = 'Request failed'): string {
  const normalized = normalizeApiError(err);
  const payload = normalized.payload;

  if (payload && typeof payload === 'object') {
    if (typeof (payload as any).error === 'string' && (payload as any).error) {
      return (payload as any).error;
    }
    if (typeof (payload as any).message === 'string' && (payload as any).message) {
      return (payload as any).message;
    }
  }

  if (normalized.responseText) {
    return normalized.responseText;
  }

  if (normalized.message) {
    return normalized.message;
  }

  return fallback;
}

export function getUserFacingApiErrorMessage(err: unknown, fallback: string = 'Request failed'): string {
  const normalized = normalizeApiError(err);

  if (normalized.isAuthError || normalized.status === 401) {
    return 'Your session expired. Please sign in again.';
  }

  if (normalized.status === 403) {
    return 'You do not have permission to perform this action.';
  }

  if (normalized.status === 404) {
    return 'The requested resource could not be found.';
  }

  if (normalized.isNetworkError) {
    return 'Network connection lost. Please check your internet connection and try again.';
  }

  return getApiErrorMessage(normalized, fallback);
}

export function logApiFailure(context: { url: string; method?: string }, err: unknown): void {
  if (isAbortLikeError(err)) {
    return;
  }

  const normalized = normalizeApiError(err);
  console.warn('API request failed', {
    url: context.url,
    method: context.method || 'GET',
    status: normalized.status ?? null,
    kind: normalized.kind,
    message: normalized.message,
  });
}

export class BaseAPIClient {
  protected API_CONFIG: {
    development: Endpoints;
    production: Endpoints;
  };

  constructor(endpoints: Endpoints = {}) {
    this.API_CONFIG = {
      development: endpoints,
      production: endpoints
    };
  }

  /**
     * Check if running in development mode
     */
  isDevelopment(): boolean {
    if (typeof location === 'undefined') {
      return false;
    }
    return location.hostname === 'localhost' || location.hostname === '127.0.0.1';
  }

  /**
     * Get API endpoints for current environment
     */
  getEndpoints(): Endpoints {
    return this.isDevelopment() ? this.API_CONFIG.development : this.API_CONFIG.production;
  }

  /**
     * Core API call method with authentication and standardized error handling
     */
  async apiCall<T = any>(endpoint: string, options: APIOptions = {}): Promise<T> {
    const API_ENDPOINTS = this.getEndpoints();

    let baseUrl = API_ENDPOINTS[endpoint];
    if (!baseUrl) {
      throw new Error(`Unknown API endpoint: ${endpoint}`);
    }

    // Handle path parameters if provided
    if (options.pathParams && Array.isArray(options.pathParams)) {
      const params = options.pathParams.join('/');
      baseUrl = `${baseUrl}/${params}`;
    }

    let fullUrl = options.endpoint ? `${baseUrl}/${options.endpoint}` : baseUrl;
    
    // Handle query parameters if provided
    if (options.queryParams && typeof options.queryParams === 'object') {
      const searchParams = new URLSearchParams();
      Object.entries(options.queryParams).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, String(value));
        }
      });
      const queryString = searchParams.toString();
      if (queryString) {
        fullUrl = `${fullUrl}${fullUrl.includes('?') ? '&' : '?'}${queryString}`;
      }
    }

    const { method = 'GET', headers = {}, ...fetchOptions } = options;

    if (fetchOptions.body && typeof fetchOptions.body === 'object' && !(fetchOptions.body instanceof FormData) && !(fetchOptions.body instanceof Blob)) {
      fetchOptions.body = JSON.stringify(fetchOptions.body);
    }

    try {
      const finalHeaders = this.prepareHeaders(headers as Record<string, string>, fetchOptions.body);

      const response = await authGuard.authenticatedFetch(fullUrl, {
        method,
        headers: finalHeaders,
        ...fetchOptions
      });

      if (options.rawResponse) {
        return response as unknown as T;
      }

      return await this.handleResponse<T>(response);

    } catch (error) {
      if (!isAbortLikeError(error)) {
        logApiFailure({ url: fullUrl, method }, error);
      }
      throw this.handleError(error);
    }
  }

  /**
     * Prepare headers for the request
     */
  protected prepareHeaders(headers: Record<string, string>, body: any): Record<string, string> {
    const finalHeaders = { ...headers };

    // Handle Content-Type for different body types
    if (!(body instanceof FormData) && finalHeaders['Content-Type'] !== null) {
      if (!finalHeaders['Content-Type']) {
        finalHeaders['Content-Type'] = 'application/json';
      }
    }

    // Remove Content-Type if explicitly set to null
    if (finalHeaders['Content-Type'] === null as any) {
      delete finalHeaders['Content-Type'];
    }

    return finalHeaders;
  }

  /**
     * Handle API response with standardized parsing
     */
  protected async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      throw await buildApiErrorFromResponse(response);
    }

    return await parseApiResponse<T>(response);
  }

  /**
     * Handle API errors with consistent error formatting
     */
  protected handleError(error: any): Error {
    return normalizeApiError(error);
  }

  /**
     * Check if user is authenticated
     * Subclasses can override this method based on their auth state management
     */
  isAuthenticated(): boolean {
    const authState = authGuard.getAuthState();
    return authState.isAuthenticated;
  }

  /**
     * Wait for authentication to be ready
     * Useful for initialization scenarios
     */
  async waitForAuth(): Promise<AuthState> {
    return new Promise((resolve) => {
      const checkAuth = () => {
        const authState = authGuard.getAuthState();
        if (authState.authReady) {
          resolve(authState);
        } else {
          setTimeout(checkAuth, 100);
        }
      };
      checkAuth();
    });
  }

  /**
     * Extract user-friendly error message from API error
     */
  getApiErrorMessage(err: any, fallback: string = 'Request failed'): string {
    return getApiErrorMessage(err, fallback);
  }

  /**
     * Redirect to authentication page
     * Subclasses can override this method for custom behavior
     */
  redirectToAuth(): void {
    // Default implementation - subclasses should override if needed
    console.warn('Authentication required - please implement redirectToAuth in subclass');
  }
}
